import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import * as moment from 'moment-timezone';
import { RestfulEndpoint } from '../restful-endpoint.service';
import { environment } from '../../../environments/environment';
import {
  AddParentToMultipleProjectsResponse,
  ProjectNamesDataModel,
  ProjectNamesResponseModel,
  ProjectTagsResponseModel,
  UpdateProjectInfoData,
  UpdateProjectResponseModel,
} from '../../models/projects-refactored';
import { Observable } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import {
  ProjectReportsResponseRefactored,
  All360ReportsResponse,
  All360ReportsFilters,
  ReportsPublishedDateFilters,
  ReportSharingOptionsResponse,
  ShareReportToOriginalPayload,
  ShareReportToOriginalResponse,
  ProjectSharedReportsResponse,
  ProjectReportOrderPayload,
} from '../../models/reports-refactored.model';
import { AccessLevel } from '../../core/user/user.model';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectsRefactoredService {
  projectNamesList: any[];
  projectNamesLoading: boolean = false;
  isTagsLoading: boolean = false;
  totalProjectNames: number = 0;
  totalTagsCount: number = 0;
  currentPage: number;
  totalPages: number;

  constructor(
    private endPoint: RestfulEndpoint,
    private translateService: TranslocoService,
    private userService: UserService,
    private http: HttpClient,
  ) {}

  /*
   * the new update method
   * for updating project info
   * */
  async updateProjectInfo(projectId: string, body: any): Promise<UpdateProjectInfoData> {
    if (!projectId) {
      return;
    }
    return new Promise((resolve, reject) => {
      this.api_updateProjectInfo(projectId, body).subscribe(
        (res: UpdateProjectResponseModel) => {
          resolve(res.data);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  async fetchProjectNames() {
    let projectNamesParams = {
      page: 1,
      per_page: 1000,
      depth: 2,
      is_archived: 0 as const,
    };
    // fetching projects names list
    await this.fetchProjectsListNames(projectNamesParams).then((res) => {
      this.projectNamesList = this.prepareFlatProjectNamesList(res);
    });
  }

  async addParentToMultipleProjects(body: any): Promise<AddParentToMultipleProjectsResponse> {
    return new Promise((resolve, reject) => {
      this.api_addParentMultipleProject(body).subscribe(
        (res: AddParentToMultipleProjectsResponse) => {
          resolve(res);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  async fetchProjectsListNames(params: {
    page: number;
    per_page: number;
    depth: number;
    is_archived?: 0 | 1;
  }): Promise<ProjectNamesDataModel[]> {
    this.projectNamesLoading = true;
    return new Promise((resolve, reject) => {
      this.api_getProjectsListNames(params).subscribe(
        (projects: ProjectNamesResponseModel) => {
          this.totalProjectNames = projects.meta_data.total_items;
          this.currentPage = projects.meta_data.current_page;
          this.totalPages = projects.meta_data.total_pages;
          resolve(projects.data);
          this.projectNamesLoading = false;
        },
        (error) => {
          reject(error);
          this.projectNamesLoading = false;
        },
      );
    });
  }

  async getProjectTags(params: {
    page: number;
    per_page: number;
  }): Promise<ProjectTagsResponseModel> {
    this.isTagsLoading = true;
    return new Promise((resolve, reject) => {
      this.api_getHashTags(params).subscribe(
        (res: ProjectTagsResponseModel) => {
          this.totalTagsCount = res.meta_data.total_items;
          resolve(res);
          this.isTagsLoading = false;
        },
        (error) => {
          reject(error);
          this.isTagsLoading = false;
        },
      );
    });
  }

  getProjectReportsRefactored(
    projectId: string,
    per_page = 20,
    page = 1,
    report_type?: string,
    search?: string,
    filters: ReportsPublishedDateFilters = {},
  ): Observable<ProjectReportsResponseRefactored> {
    const params = new URLSearchParams();
    params.append('per_page', per_page.toString());
    params.append('page', page.toString());
    report_type ? params.append('report_type', report_type) : null;
    search ? params.append('report_name', search) : null;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return this.endPoint.get(`projects/${projectId}/reports?${params.toString()}`);
  }

  // Cumulative variant: for a cluster/region (level <= 2), the backend aggregates the
  // 360 reports of the whole visible sub-project subtree instead of the single project.
  getCumulativeProjectReportsRefactored(
    projectId: string,
    per_page = 20,
    page = 1,
    report_type?: string,
    search?: string,
    filters: ReportsPublishedDateFilters = {},
  ): Observable<ProjectReportsResponseRefactored> {
    const params = new URLSearchParams();
    params.append('per_page', per_page.toString());
    params.append('page', page.toString());
    report_type ? params.append('report_type', report_type) : null;
    search ? params.append('report_name', search) : null;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return this.endPoint.get(`projects/${projectId}/reports/cumulative?${params.toString()}`);
  }

  getProjectSharedReports(
    projectId: string,
    per_page = 20,
    page = 1,
  ): Observable<ProjectSharedReportsResponse> {
    const params = new URLSearchParams();
    params.append('per_page', per_page.toString());
    params.append('page', page.toString());
    return this.endPoint.get(`projects/${projectId}/shared-reports?${params.toString()}`);
  }

  // PC-2351: move a report within the project's manual order (after/before another report, or
  // to the top when neither anchor is sent).
  reorderProjectReport(projectId: string, body: ProjectReportOrderPayload): Observable<unknown> {
    return this.endPoint.post(`${projectId}/report-order`, body, null, 'projects');
  }

  // PC-2351: clear the project's manual order so it falls back to newest published first.
  resetProjectReportOrder(projectId: string): Observable<unknown> {
    return this.endPoint.post(`${projectId}/report-order/reset`, {}, null, 'projects');
  }

  shareProjectLinks(project_id: string, body: any): Observable<ProjectReportsResponseRefactored> {
    return this.endPoint.post(`${project_id}/share-link`, body, null, 'projects');
  }

  validateProjectKey(key: string): Observable<{ data: { suggested_key: string } }> {
    return this.endPoint.post('v2/projects/validate-key', { key });
  }

  getProjectV2(projectId: string): Observable<any> {
    return this.endPoint.get(projectId, null, 'projects');
  }

  getReportSharingOptions(reportId: string): Observable<ReportSharingOptionsResponse> {
    return this.endPoint.get(`${reportId}/sharing-options`, null, 'reports');
  }

  shareReportToOriginal(
    reportId: string,
    payload: ShareReportToOriginalPayload,
  ): Observable<ShareReportToOriginalResponse> {
    return this.endPoint.post(`${reportId}/share-to-original`, payload, null, 'reports');
  }

  // async getProject100ReportsRefactored(
  //   projectId: string,
  //   pageNo: number,
  // ): Promise<projectReportsResponse> {
  //   if (!projectId) {
  //     return;
  //   }
  //   this.projectsLoading = true;
  //   let params = {
  //     page: pageNo,
  //     per_page: 20,
  //   };
  //   return new Promise((resolve, reject) => {
  //     this.api_getProjectReports(projectId, params).subscribe(
  //       (res: projectReportsResponse) => {
  //         this.projectsLoading = false;
  //         resolve(res);
  //       },
  //       (error: any) => {
  //         this.projectsLoading = false;
  //         reject(error);
  //       },
  //     );
  //   });
  // }

  prepareFlatProjectNamesList(projects: ProjectNamesDataModel[]) {
    const flatFolders = [];
    const lang = this.translateService.getActiveLang();
    const isLtr = lang === 'en' || lang === 'ru';
    const arrowIcon = isLtr ? '&#9205; ' : '&#9204; ';
    const insideIcon = isLtr ? '&#9495;&#9473;' : '&#9497;&#9473;';
    projects.forEach((e) => {
      flatFolders.push({
        project_name: arrowIcon + e.project_name,
        id: e.id,
      });
      if (e.children) {
        e.children.forEach((l) => {
          flatFolders.push({
            project_name: insideIcon + '&#160;&#160;  ' + l.project_name,
            id: l.id,
          });
        });
      }
    });

    return flatFolders;
  }

  /*
   * the new API for updating project info
   * */
  private api_updateProjectInfo(project_Id: string, body: any): any {
    return this.endPoint.post(project_Id, body, null, 'projects');
  }

  private api_addParentMultipleProject(body: any): any {
    return this.endPoint.post('add-parent-multiable-projects', body, null, 'projects');
  }

  private api_getProjectsListNames(queryParams): Observable<any> {
    return this.endPoint.get('names', queryParams, 'projects');
  }

  private api_getHashTags(queryParams): Observable<any> {
    return this.endPoint.get('tags', queryParams, 'clients');
  }

  private api_getProjectReports(projectID, params): Observable<any> {
    return this.endPoint.get('reports', params, `projects/${projectID}`);
  }

  private api_getAll360Reports(
    per_page: number = 20,
    page: number = 1,
    filters: All360ReportsFilters = {},
  ): Observable<All360ReportsResponse> {
    const params: { [key: string]: string | number | boolean } = {
      per_page: per_page,
      page: page,
      ...this.compactAll360Filters(filters),
    };

    return this.endPoint.get('reports', params, 'clients');
  }

  /**
   * Drops empty values so an unset filter is omitted from the query string rather than
   * sent as `null` or a blank string, which the API rejects on the date fields.
   */
  private compactAll360Filters(filters: All360ReportsFilters): Record<string, string> {
    return Object.entries(filters).reduce(
      (compact, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          compact[key] = String(value);
        }
        return compact;
      },
      {} as Record<string, string>,
    );
  }

  /**
   * Returns the XLSX bytes for every report matching the filters — the endpoint answers
   * with a file rather than the V2 envelope, and ignores pagination.
   *
   * It bypasses RestfulEndpoint because that has no responseType on GET, so the headers
   * are set here. userLang is required: it picks the heading language and whether the
   * sheet is written right-to-left.
   */
  exportAll360Reports(filters: All360ReportsFilters = {}): Observable<Blob> {
    return this.http.get(`${environment.api_base_url}/v2/clients/reports/export`, {
      headers: this.exportHeaders(),
      params: this.compactAll360Filters(filters),
      responseType: 'blob',
    });
  }

  /**
   * PC-2357: XLSX of the cluster/region (cumulative) 360 reports list, every page, narrowed by
   * the same From/To dates the list uses. Same blob/headers contract as exportAll360Reports.
   */
  exportCumulativeProjectReports(
    projectId: string,
    filters: ReportsPublishedDateFilters = {},
  ): Observable<Blob> {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });

    return this.http.get(
      `${environment.api_base_url}/v2/projects/${projectId}/reports/cumulative/export`,
      { headers: this.exportHeaders(), params, responseType: 'blob' },
    );
  }

  /**
   * PC-2278: XLSX of a single project's 360 Reports Management list, every page, narrowed by the
   * same From/To dates the list uses. Same blob/headers contract as exportAll360Reports.
   */
  exportProjectReports(
    projectId: string,
    filters: ReportsPublishedDateFilters = {},
  ): Observable<Blob> {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });

    return this.http.get(`${environment.api_base_url}/v2/projects/${projectId}/reports/export`, {
      headers: this.exportHeaders(),
      params,
      responseType: 'blob',
    });
  }

  private exportHeaders(): HttpHeaders {
    return new HttpHeaders({
      userLang: localStorage.getItem('lang') || 'en',
      timeZone: moment.tz.guess(),
      platform: 'web',
    });
  }

  async getPermission(access: AccessLevel): Promise<boolean> {
    return this.userService.permissions[access];
  }

  async getAll360Reports(
    per_page: number = 20,
    page: number = 1,
    filters: All360ReportsFilters = {},
  ): Promise<All360ReportsResponse> {
    return new Promise((resolve, reject) => {
      this.api_getAll360Reports(per_page, page, filters).subscribe(
        (res: All360ReportsResponse) => {
          resolve(res);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }
}
